import { readMonoWindowFromSink, rms } from "../../../engine/clips/utils/cut-analysis";
import { isScriptClip, ScriptLayerClip } from "../../../engine/clips";
import type { CutTranscriptMode } from "../../../engine/clips/script/script.interfaces";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";

type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

type PostAiRetakesFn = (args: {
    projectId: string;
    policy?: {
        mode?: "conservative";
        minConfidence?: number;
        maxGapSec?: number;
        requireSameSource?: boolean;
        requireSameSpeakerWhenAvailable?: boolean;
    };
    candidates: Array<{
        id: string;
        sourceIndex: number;
        speakerKey: string;
        gapSec: number;
        lexicalSimilarity: number;
        heuristicScore: number;
        restartCueNearby: boolean;
        first: { segmentId: string; startSec: number; endSec: number; text: string };
        second: { segmentId: string; startSec: number; endSec: number; text: string };
        bridgeCueText?: string;
        contextBefore?: string;
        contextAfter?: string;
    }>;
}) => Promise<
    | {
          decisions?: Array<{
              candidateId?: string;
              decision?: "retake" | "not_retake";
              remove?: "first" | "second";
              confidence?: number;
              reason?: string;
          }>;
          retakes?: Array<{
              candidateId?: string;
              remove?: "first" | "second";
              confidence?: number;
              reason?: string;
          }>;
      }
    | undefined
>;

type RetakeWord = {
    text: string;
    startSec: number;
    endSec: number;
    srcStartSec: number;
    srcEndSec: number;
    sourceIndex: number;
    speakerKey: string;
    diarizationSpeakerId?: string;
};

type RetakeSegment = {
    id: string;
    sourceIndex: number;
    speakerKey: string;
    startSec: number;
    endSec: number;
    srcStartSec: number;
    srcEndSec: number;
    text: string;
    normalizedText: string;
    wordCount: number;
    hasRestartCue: boolean;
    contextBefore?: string;
    contextAfter?: string;
};

type RetakeCandidate = {
    id: string;
    firstSegment: RetakeSegment;
    secondSegment: RetakeSegment;
    bridgeCueSegment?: RetakeSegment;
    sourceIndex: number;
    speakerKey: string;
    gapSec: number;
    lexicalSimilarity: number;
    heuristicScore: number;
    restartCueNearby: boolean;
    suggestedRemove: "first" | "second";
};

type ParsedAiDecision = {
    candidateId: string;
    decision: "retake" | "not_retake";
    remove: "first" | "second";
    confidence?: number;
    reason?: string;
};

export type RetakeDetection = {
    id: string;
    candidateId: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    speakerKey: string;
    confidence: number;
    reason: string;
    remove: "first" | "second";
    removedText: string;
    keptText: string;
    lexicalSimilarity: number;
    restartCueNearby: boolean;
};

export type DetectRetakesResult = {
    detections: RetakeDetection[];
    stats: {
        totalWords: number;
        totalSegments: number;
        totalCandidates: number;
        usedLlm: boolean;
    };
};

type DetectRetakesArgs = {
    getTranscript: GetTranscriptFn;
    postAiRetakes?: PostAiRetakesFn;
    projectId: string;
    scriptClipId?: string | null;
    minConfidence?: number;
    maxGapSec?: number;
    maxCandidates?: number;
    useAudioVeto?: boolean;
};

type ApplyRetakesArgs = {
    detections: RetakeDetection[];
    transcriptMode?: CutTranscriptMode;
};

const EPS = 1e-3;
const MIN_ENERGY_RATIO = 0.2;
const DEFAULT_MAX_GAP_SEC = 10;
const DEFAULT_MIN_CONFIDENCE = 0.9;
const DEFAULT_MAX_CANDIDATES = 40;
const DEFAULT_AUDIO_VETO = true;
const AUDIO_VETO_LOW_ENVELOPE_SIM = 0.22;
const AUDIO_VETO_BORDERLINE_CONF = 0.94;
const AUDIO_SAMPLE_RATE = 48000;

const RESTART_CUE_PATTERNS: RegExp[] = [
    /\bcut\b/i,
    /\bstart(?:ing)? (?:again|over)\b/i,
    /\bagain from the top\b/i,
    /\blet me (?:start|do) (?:that )?again\b/i,
    /\bretake\b/i,
    /\btake two\b/i,
    /\bone more time\b/i,
    /\bscratch that\b/i,
    /\brestart\b/i,
    /\bredo\b/i,
];

const toFinite = (value: any, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const clamp = (value: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, value));

const joinWordsAsText = (words: Array<{ text: string }>): string => {
    let out = "";
    const noSpaceBefore = (t: string) => /^[.,!?;:)\]]$/.test(t);
    for (const w of words) {
        const t = String(w?.text ?? "");
        if (!t) continue;
        if (out && !noSpaceBefore(t)) out += " ";
        out += t;
    }
    return out.trim();
};

const normalizeText = (value: string): string =>
    String(value ?? "")
        .toLowerCase()
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[^a-z0-9'\s]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const tokenize = (value: string): string[] => {
    const norm = normalizeText(value);
    if (!norm) return [];
    return norm.split(" ").filter(Boolean);
};

const countCommonTokens = (a: string[], b: string[]): number => {
    if (!a.length || !b.length) return 0;
    const freq = new Map<string, number>();
    for (const t of a) freq.set(t, (freq.get(t) ?? 0) + 1);
    let common = 0;
    for (const t of b) {
        const c = freq.get(t) ?? 0;
        if (c <= 0) continue;
        common += 1;
        freq.set(t, c - 1);
    }
    return common;
};

const tokenSimilarity = (a: string, b: string): number => {
    const ta = tokenize(a);
    const tb = tokenize(b);
    if (!ta.length || !tb.length) return 0;
    const common = countCommonTokens(ta, tb);
    return common / Math.max(ta.length, tb.length, 1);
};

const prefixTokenSimilarity = (a: string, b: string): number => {
    const ta = tokenize(a);
    const tb = tokenize(b);
    if (!ta.length || !tb.length) return 0;
    const minLen = Math.min(ta.length, tb.length);
    let shared = 0;
    for (let i = 0; i < minLen; i++) {
        if (ta[i] !== tb[i]) break;
        shared += 1;
    }
    return shared / Math.max(minLen, 1);
};

const levenshteinSimilarity = (a: string, b: string): number => {
    const s = normalizeText(a);
    const t = normalizeText(b);
    if (!s && !t) return 1;
    if (!s || !t) return 0;
    const m = s.length;
    const n = t.length;
    const dp = new Array(n + 1);
    for (let j = 0; j <= n; j++) dp[j] = j;
    for (let i = 1; i <= m; i++) {
        let prevDiag = dp[0];
        dp[0] = i;
        for (let j = 1; j <= n; j++) {
            const temp = dp[j];
            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
            dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prevDiag + cost);
            prevDiag = temp;
        }
    }
    const dist = dp[n];
    return 1 - dist / Math.max(m, n, 1);
};

const isSentenceBoundaryToken = (text: string): boolean => /[.!?]["')\]]?$/.test(String(text ?? "").trim());

const hasRestartCue = (text: string): boolean => {
    const v = String(text ?? "").trim();
    if (!v) return false;
    return RESTART_CUE_PATTERNS.some((p) => p.test(v));
};

const buildSegments = (words: RetakeWord[]): RetakeSegment[] => {
    if (!words.length) return [];

    const segments: RetakeSegment[] = [];
    let current: RetakeWord[] = [];

    const flush = () => {
        if (!current.length) return;
        const first = current[0];
        const last = current[current.length - 1];
        const text = joinWordsAsText(current);
        const normalized = normalizeText(text);
        if (normalized) {
            segments.push({
                id: `seg_${segments.length}`,
                sourceIndex: first.sourceIndex,
                speakerKey: first.speakerKey,
                startSec: first.startSec,
                endSec: last.endSec,
                srcStartSec: first.srcStartSec,
                srcEndSec: last.srcEndSec,
                text,
                normalizedText: normalized,
                wordCount: current.length,
                hasRestartCue: hasRestartCue(text),
            });
        }
        current = [];
    };

    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        const prev = i > 0 ? words[i - 1] : null;
        const gap = prev ? Math.max(0, Number(w.startSec ?? 0) - Number(prev.endSec ?? prev.startSec ?? 0)) : 0;
        const prevEndsSentence = prev ? isSentenceBoundaryToken(prev.text) : false;

        const shouldBreak =
            current.length > 0 &&
            (prev == null ||
                prev.sourceIndex !== w.sourceIndex ||
                prev.speakerKey !== w.speakerKey ||
                gap >= 1.15 ||
                (prevEndsSentence && gap >= 0.45) ||
                current.length >= 32);

        if (shouldBreak) flush();
        current.push(w);
    }
    flush();

    for (let i = 0; i < segments.length; i++) {
        const before = i > 0 ? segments[i - 1]?.text : "";
        const after = i < segments.length - 1 ? segments[i + 1]?.text : "";
        segments[i].contextBefore = before || undefined;
        segments[i].contextAfter = after || undefined;
    }

    return segments;
};

const chooseBestOverride = (matching: any[]): any => {
    const overrideSpecificity = (o: any): number => {
        let score = 0;
        if (o?.sourceIndex != null) score += 1;
        if ((o as any)?.diarizationSpeakerId) score += 2;
        return score;
    };
    const overrideDuration = (o: any): number => {
        const start = Number(o?.startSec ?? 0);
        const end = Number(o?.endSec ?? 0);
        return Math.max(0, end - start);
    };
    const chooseBetter = (a: any, b: any): any => {
        const durA = overrideDuration(a);
        const durB = overrideDuration(b);
        if (durA !== durB) return durB < durA ? b : a;

        const specA = overrideSpecificity(a);
        const specB = overrideSpecificity(b);
        if (specA !== specB) return specB > specA ? b : a;

        if (a.startSec !== b.startSec) return b.startSec > a.startSec ? b : a;
        return String(b.id ?? "") > String(a.id ?? "") ? b : a;
    };
    return matching.reduce(chooseBetter);
};

const getEffectiveSpeakerKeyForWord = (args: {
    sourceIndex: number;
    clipStartSec: number;
    diarizationSpeakerId?: string;
}): string => {
    const speakerOverrides = Array.isArray(ScriptLayerStore.speakerOverrides)
        ? ScriptLayerStore.speakerOverrides
        : [];

    const matching = speakerOverrides.filter((override: any) => {
        if (args.clipStartSec < Number(override?.startSec ?? 0) || args.clipStartSec >= Number(override?.endSec ?? 0)) {
            return false;
        }
        if (override?.sourceIndex != null && Number(override.sourceIndex) !== args.sourceIndex) return false;
        const diar = (override as any)?.diarizationSpeakerId;
        if (diar && args.diarizationSpeakerId && String(diar) !== String(args.diarizationSpeakerId)) return false;
        if (diar && !args.diarizationSpeakerId) return false;
        return true;
    });

    if (matching.length) {
        const o = chooseBestOverride(matching);
        const speakerId = typeof o?.speakerId === "string" ? String(o.speakerId) : "";
        if (speakerId) return `speaker-${speakerId}`;
        const overrideId = String(o?.id ?? "").trim();
        if (overrideId) return `override-${overrideId}`;
    }

    if (args.diarizationSpeakerId) return `source-${args.sourceIndex}-${String(args.diarizationSpeakerId)}`;
    return `source-${args.sourceIndex}`;
};

const buildEntrySegments = (entry: any, sourceDuration: number): Array<{
    offsetSec: number;
    playbackRate: number;
    rangeInSec: number;
    rangeOutSec: number;
}> => {
    const out: Array<{ offsetSec: number; playbackRate: number; rangeInSec: number; rangeOutSec: number }> = [];
    const segmentsRaw: any[] = Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : [];
    if (segmentsRaw.length > 0) {
        for (const seg of segmentsRaw) {
            const segOffset = toFinite(seg?.offsetSec, 0);
            const segRate = toFinite(seg?.playbackRate ?? entry?.playbackRate, 1) || 1;
            const segIn = toFinite(seg?.range?.inSec, 0);
            const segOut = toFinite(seg?.range?.outSec, 0);
            if (!Number.isFinite(segOffset) || !Number.isFinite(segRate) || segRate <= 0) continue;
            if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
            out.push({ offsetSec: segOffset, playbackRate: segRate, rangeInSec: segIn, rangeOutSec: segOut });
        }
    } else {
        const rangeInSec = toFinite(entry?.range?.inSec, 0);
        const rangeOutSec = toFinite(entry?.range?.outSec, sourceDuration > 0 ? sourceDuration : Infinity);
        const offsetSec = toFinite(entry?.offsetSec, 0);
        const playbackRate = toFinite(entry?.playbackRate, 1) || 1;
        out.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
    }
    return out;
};

const buildEditedTranscriptWordsForRetakes = async (args: {
    getTranscript: GetTranscriptFn;
    projectId: string;
    scriptClipId?: string | null;
}): Promise<{ words: RetakeWord[]; scriptClip: ScriptLayerClip | null }> => {
    const projectId = String(args.projectId ?? "");
    if (!projectId) return { words: [], scriptClip: null };

    let inferredScriptClipId = "";
    if (!args.scriptClipId) {
        try {
            inferredScriptClipId = String((CompositionService.composition as any)?.scriptLayer?.clipId ?? "");
        } catch {
            inferredScriptClipId = "";
        }
    }
    const scriptClipId = String(args.scriptClipId ?? "") || inferredScriptClipId;
    if (!scriptClipId) return { words: [], scriptClip: null };

    const clip = CompositionService.getClipById(scriptClipId);
    if (!clip || !isScriptClip(clip)) return { words: [], scriptClip: null };

    const scriptClip = clip as ScriptLayerClip;
    const audioEntries = scriptClip.getAudioEntries();
    const getTranscript = args.getTranscript;

    const scriptSegments: Array<{ srcStartSec: number; srcEndSec: number; isGap?: boolean }> =
        Array.isArray((scriptClip as any).scriptSegments) && (scriptClip as any).scriptSegments.length > 0
            ? ((scriptClip as any).scriptSegments as any[])
            : [];

    const isWordInPlayableSection = (clipStart: number, clipEnd: number): boolean => {
        if (!scriptSegments.length) return true;
        for (const seg of scriptSegments) {
            if ((seg as any).isGap) continue;
            if (clipStart >= Number(seg.srcStartSec ?? 0) && clipEnd <= Number(seg.srcEndSec ?? 0)) {
                return true;
            }
        }
        return false;
    };

    const transcriptHiddenRanges: Array<{ startSec: number; endSec: number }> = Array.isArray(
        (scriptClip as any).transcriptHiddenRanges,
    )
        ? ((scriptClip as any).transcriptHiddenRanges as any[])
        : [];
    const isHiddenAt = (clipTime: number): boolean => {
        if (!transcriptHiddenRanges.length) return false;
        for (const r of transcriptHiddenRanges) {
            const start = Number((r as any)?.startSec ?? 0);
            const end = Number((r as any)?.endSec ?? 0);
            if (clipTime >= start && clipTime < end) return true;
        }
        return false;
    };

    const words: RetakeWord[] = [];

    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
        const entry: any = audioEntries[sourceIndex];
        const entryGain = Number(entry?.gain ?? 1);
        if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;

        const assetId = String(entry?.assetId ?? "");
        if (!assetId) continue;

        const trackRecord = ProjectAssetsStore.projectTracks.find(
            (t: any) => t?.id === assetId || t?.trackId === assetId,
        );
        const transcriptId = String(trackRecord?.activeTranscriptId ?? "");
        if (!transcriptId) continue;

        const transcriptJson = await getTranscript({ transcriptId, projectId });
        if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) continue;

        const sourceDuration = Number(trackRecord?.duration ?? 0) || 0;
        const entrySegments = buildEntrySegments(entry, sourceDuration);

        for (const w of transcriptJson.words) {
            if ((w?.type ?? "word") !== "word") continue;
            const energy = Number((w as any)?.energy?.ratio ?? NaN);
            if (Number.isFinite(energy) && energy > 0 && energy < MIN_ENERGY_RATIO) continue;

            const wordStartSrc = toFinite(w?.t0 ?? w?.start, 0);
            const wordEndSrc = toFinite(w?.t1 ?? w?.end, wordStartSrc);
            if (!Number.isFinite(wordStartSrc) || !Number.isFinite(wordEndSrc) || wordEndSrc < wordStartSrc) continue;

            const rawText = String(w?.text ?? w?.word ?? "").trim();
            if (!rawText) continue;

            let mapped: { startSec: number; endSec: number; srcStartSec: number; srcEndSec: number } | null = null;
            for (const seg of entrySegments) {
                if (wordStartSrc < seg.rangeInSec - EPS) continue;
                if (wordEndSrc > seg.rangeOutSec + EPS) continue;
                const clipStart = seg.offsetSec + (wordStartSrc - seg.rangeInSec) / seg.playbackRate;
                const clipEnd = seg.offsetSec + (wordEndSrc - seg.rangeInSec) / seg.playbackRate;
                mapped = { startSec: clipStart, endSec: clipEnd, srcStartSec: wordStartSrc, srcEndSec: wordEndSrc };
                break;
            }
            if (!mapped) continue;
            if (!isWordInPlayableSection(mapped.startSec, mapped.endSec)) continue;
            if (isHiddenAt(mapped.startSec)) continue;

            const corrected =
                ScriptLayerStore.getWordCorrectionText({
                    sourceIndex,
                    srcStartSec: mapped.srcStartSec,
                    srcEndSec: mapped.srcEndSec,
                }) ?? null;

            const text = corrected || rawText;
            const diarizationSpeakerIdRaw = w?.speakerId ?? w?.speaker_id;
            const diarizationSpeakerId =
                diarizationSpeakerIdRaw != null ? String(diarizationSpeakerIdRaw) : undefined;
            const speakerKey = getEffectiveSpeakerKeyForWord({
                sourceIndex,
                clipStartSec: mapped.startSec,
                diarizationSpeakerId,
            });

            words.push({
                text,
                startSec: mapped.startSec,
                endSec: mapped.endSec,
                srcStartSec: mapped.srcStartSec,
                srcEndSec: mapped.srcEndSec,
                sourceIndex,
                speakerKey,
                diarizationSpeakerId,
            });
        }
    }

    words.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
    return { words, scriptClip };
};

const buildCandidate = (args: {
    firstSegment: RetakeSegment;
    secondSegment: RetakeSegment;
    bridgeCueSegment?: RetakeSegment;
}): RetakeCandidate | null => {
    const a = args.firstSegment;
    const b = args.secondSegment;
    if (!a || !b) return null;
    if (b.startSec <= a.startSec + EPS) return null;
    if (a.sourceIndex !== b.sourceIndex) return null;
    if (a.speakerKey !== b.speakerKey) return null;

    const gapSec = Math.max(0, Number(b.startSec ?? 0) - Number(a.endSec ?? a.startSec ?? 0));
    if (gapSec > 12) return null;

    const lexical = clamp(
        0.62 * tokenSimilarity(a.text, b.text) +
            0.26 * levenshteinSimilarity(a.text, b.text) +
            0.12 * prefixTokenSimilarity(a.text, b.text),
        0,
        1,
    );

    const restartCueNearby = Boolean(a.hasRestartCue || b.hasRestartCue || args.bridgeCueSegment?.hasRestartCue);
    const wordCountDelta = Math.abs(Number(a.wordCount ?? 0) - Number(b.wordCount ?? 0));
    const durationA = Math.max(0, Number(a.endSec ?? 0) - Number(a.startSec ?? 0));
    const durationB = Math.max(0, Number(b.endSec ?? 0) - Number(b.startSec ?? 0));
    const durationDelta = Math.abs(durationA - durationB);

    let heuristic = lexical * 0.74;
    if (gapSec <= 1.5) heuristic += 0.11;
    else if (gapSec <= 4) heuristic += 0.06;
    if (wordCountDelta <= 2) heuristic += 0.06;
    else if (wordCountDelta <= 5) heuristic += 0.03;
    if (durationDelta <= 1.1) heuristic += 0.04;
    if (restartCueNearby) heuristic += 0.17;
    if (!restartCueNearby && lexical < 0.68) heuristic -= 0.2;
    heuristic = clamp(heuristic, 0, 0.995);

    const shouldKeep =
        (lexical >= 0.72 && gapSec <= 10) ||
        (lexical >= 0.64 && restartCueNearby && gapSec <= 10) ||
        (prefixTokenSimilarity(a.text, b.text) >= 0.86 && gapSec <= 5);

    if (!shouldKeep) return null;

    const secondLooksLikeCueOnly = b.hasRestartCue && b.wordCount <= 6 && lexical < 0.78;
    const suggestedRemove: "first" | "second" = secondLooksLikeCueOnly ? "second" : "first";

    return {
        id: `${a.id}__${b.id}${args.bridgeCueSegment ? `__${args.bridgeCueSegment.id}` : ""}`,
        firstSegment: a,
        secondSegment: b,
        bridgeCueSegment: args.bridgeCueSegment,
        sourceIndex: a.sourceIndex,
        speakerKey: a.speakerKey,
        gapSec,
        lexicalSimilarity: lexical,
        heuristicScore: heuristic,
        restartCueNearby,
        suggestedRemove,
    };
};

const buildRetakeCandidates = (segments: RetakeSegment[], maxGapSec: number): RetakeCandidate[] => {
    const out: RetakeCandidate[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < segments.length; i++) {
        const a = segments[i];
        if (!a) continue;
        if (a.wordCount < 2 && !a.hasRestartCue) continue;

        for (let j = i + 1; j < Math.min(segments.length, i + 4); j++) {
            const b = segments[j];
            if (!b) continue;
            if (b.startSec - a.endSec > maxGapSec + 2) break;

            // Adjacent pair
            if (j === i + 1) {
                const c = buildCandidate({ firstSegment: a, secondSegment: b });
                if (c && c.gapSec <= maxGapSec) {
                    if (!seen.has(c.id)) {
                        seen.add(c.id);
                        out.push(c);
                    }
                }
                continue;
            }

            // "A cue B" pattern where middle segment is a short restart cue.
            if (j === i + 2) {
                const mid = segments[i + 1];
                const cueBridge =
                    !!mid &&
                    mid.sourceIndex === a.sourceIndex &&
                    mid.speakerKey === a.speakerKey &&
                    mid.hasRestartCue &&
                    Math.max(0, mid.endSec - mid.startSec) <= 2.6;
                if (!cueBridge) continue;
                const c = buildCandidate({ firstSegment: a, secondSegment: b, bridgeCueSegment: mid });
                if (c && c.gapSec <= maxGapSec) {
                    if (!seen.has(c.id)) {
                        seen.add(c.id);
                        out.push(c);
                    }
                }
            }
        }
    }

    out.sort((a, b) => {
        const ta = Number(a.firstSegment.startSec ?? 0);
        const tb = Number(b.firstSegment.startSec ?? 0);
        if (ta !== tb) return ta - tb;
        return Number(b.heuristicScore ?? 0) - Number(a.heuristicScore ?? 0);
    });
    return out;
};

const parseAiDecisions = (raw: any, candidates: RetakeCandidate[]): ParsedAiDecision[] => {
    const candidateById = new Map(candidates.map((c) => [c.id, c]));
    const out: ParsedAiDecision[] = [];

    const rows: any[] = Array.isArray(raw?.decisions)
        ? raw.decisions
        : Array.isArray(raw?.retakes)
          ? raw.retakes
          : [];

    for (const row of rows) {
        const candidateId = String((row as any)?.candidateId ?? "").trim();
        if (!candidateById.has(candidateId)) continue;
        const decisionRaw = String((row as any)?.decision ?? "retake").toLowerCase();
        const decision: "retake" | "not_retake" = decisionRaw === "not_retake" ? "not_retake" : "retake";
        const removeRaw = String((row as any)?.remove ?? "").toLowerCase();
        const remove: "first" | "second" = removeRaw === "second" ? "second" : "first";
        const confidenceRaw = Number((row as any)?.confidence);
        const confidence = Number.isFinite(confidenceRaw) ? clamp(confidenceRaw, 0, 1) : undefined;
        const reason = String((row as any)?.reason ?? "").trim() || undefined;
        out.push({ candidateId, decision, remove, confidence, reason });
    }

    return out;
};

const toLlmPayload = (candidates: RetakeCandidate[], maxCandidates: number) => {
    const ranked = [...candidates]
        .sort((a, b) => Number(b.heuristicScore ?? 0) - Number(a.heuristicScore ?? 0))
        .slice(0, Math.max(1, maxCandidates));

    return ranked.map((c) => ({
        id: c.id,
        sourceIndex: c.sourceIndex,
        speakerKey: c.speakerKey,
        gapSec: Number(c.gapSec.toFixed(3)),
        lexicalSimilarity: Number(c.lexicalSimilarity.toFixed(4)),
        heuristicScore: Number(c.heuristicScore.toFixed(4)),
        restartCueNearby: c.restartCueNearby,
        first: {
            segmentId: c.firstSegment.id,
            startSec: Number(c.firstSegment.startSec.toFixed(3)),
            endSec: Number(c.firstSegment.endSec.toFixed(3)),
            text: c.firstSegment.text,
        },
        second: {
            segmentId: c.secondSegment.id,
            startSec: Number(c.secondSegment.startSec.toFixed(3)),
            endSec: Number(c.secondSegment.endSec.toFixed(3)),
            text: c.secondSegment.text,
        },
        bridgeCueText: c.bridgeCueSegment?.text || undefined,
        contextBefore: c.firstSegment.contextBefore,
        contextAfter: c.secondSegment.contextAfter,
    }));
};

const cosineSimilarity = (a: number[], b: number[]): number | null => {
    if (!a.length || !b.length || a.length !== b.length) return null;
    let dot = 0;
    let na = 0;
    let nb = 0;
    for (let i = 0; i < a.length; i++) {
        dot += a[i] * b[i];
        na += a[i] * a[i];
        nb += b[i] * b[i];
    }
    if (na <= 1e-12 || nb <= 1e-12) return null;
    return clamp(dot / (Math.sqrt(na) * Math.sqrt(nb)), -1, 1);
};

const envelopeFromSamples = (samples: Float32Array, bins: number): number[] => {
    if (!samples || samples.length === 0 || bins <= 0) return [];
    const out: number[] = [];
    const n = samples.length;
    const binSize = Math.max(1, Math.floor(n / bins));
    let maxV = 0;
    for (let b = 0; b < bins; b++) {
        const start = b * binSize;
        if (start >= n) {
            out.push(0);
            continue;
        }
        const end = b === bins - 1 ? n : Math.min(n, start + binSize);
        const value = rms(samples, start, end - start);
        out.push(value);
        if (value > maxV) maxV = value;
    }
    if (maxV <= 1e-8) return out.map(() => 0);
    return out.map((v) => v / maxV);
};

const computeEnvelopeSimilarity = async (
    scriptClip: ScriptLayerClip,
    candidate: RetakeCandidate,
): Promise<number | null> => {
    if (!scriptClip) return null;
    if (candidate.firstSegment.sourceIndex !== candidate.secondSegment.sourceIndex) return null;
    const sourceIndex = candidate.firstSegment.sourceIndex;
    const sink = scriptClip.getAudioSinkForSource(sourceIndex);
    if (!sink) return null;

    const durA = Math.max(0, candidate.firstSegment.srcEndSec - candidate.firstSegment.srcStartSec);
    const durB = Math.max(0, candidate.secondSegment.srcEndSec - candidate.secondSegment.srcStartSec);
    const sampleDuration = Math.min(durA, durB, 8);
    if (!Number.isFinite(sampleDuration) || sampleDuration < 0.55) return null;

    const sampleA = await readMonoWindowFromSink(
        sink,
        candidate.firstSegment.srcStartSec,
        sampleDuration,
        AUDIO_SAMPLE_RATE,
    );
    const sampleB = await readMonoWindowFromSink(
        sink,
        candidate.secondSegment.srcStartSec,
        sampleDuration,
        AUDIO_SAMPLE_RATE,
    );
    if (!sampleA || !sampleB || sampleA.length < 64 || sampleB.length < 64) return null;

    const envA = envelopeFromSamples(sampleA, 24);
    const envB = envelopeFromSamples(sampleB, 24);
    return cosineSimilarity(envA, envB);
};

const dedupeDetections = (detections: RetakeDetection[]): RetakeDetection[] => {
    if (!detections.length) return [];

    const byRange = new Map<string, RetakeDetection>();
    for (const d of detections) {
        const key = `${Number(d.startSec).toFixed(3)}:${Number(d.endSec).toFixed(3)}:${d.sourceIndex}`;
        const prev = byRange.get(key);
        if (!prev || d.confidence > prev.confidence) byRange.set(key, d);
    }

    const out = [...byRange.values()].sort((a, b) => a.startSec - b.startSec);
    const merged: RetakeDetection[] = [];
    for (const d of out) {
        const prev = merged[merged.length - 1];
        if (!prev) {
            merged.push(d);
            continue;
        }
        if (d.startSec < prev.endSec - EPS && d.sourceIndex === prev.sourceIndex) {
            if (d.confidence > prev.confidence) merged[merged.length - 1] = d;
            continue;
        }
        merged.push(d);
    }
    return merged;
};

export async function detectRetakes(args: DetectRetakesArgs): Promise<DetectRetakesResult> {
    const minConfidence = clamp(toFinite(args.minConfidence, DEFAULT_MIN_CONFIDENCE), 0.5, 0.99);
    const maxGapSec = Math.max(2, toFinite(args.maxGapSec, DEFAULT_MAX_GAP_SEC));
    const maxCandidates = Math.max(1, Math.floor(toFinite(args.maxCandidates, DEFAULT_MAX_CANDIDATES)));
    const useAudioVeto = args.useAudioVeto ?? DEFAULT_AUDIO_VETO;

    const { words, scriptClip } = await buildEditedTranscriptWordsForRetakes({
        getTranscript: args.getTranscript,
        projectId: args.projectId,
        scriptClipId: args.scriptClipId,
    });
    if (!words.length) {
        return {
            detections: [],
            stats: { totalWords: 0, totalSegments: 0, totalCandidates: 0, usedLlm: false },
        };
    }

    const segments = buildSegments(words);
    const candidates = buildRetakeCandidates(segments, maxGapSec);
    if (!candidates.length) {
        return {
            detections: [],
            stats: { totalWords: words.length, totalSegments: segments.length, totalCandidates: 0, usedLlm: false },
        };
    }

    let usedLlm = false;
    let llmResponseReceived = false;
    let aiDecisions: ParsedAiDecision[] = [];
    if (args.postAiRetakes) {
        const payload = toLlmPayload(candidates, maxCandidates);
        const aiRaw = await args.postAiRetakes({
            projectId: args.projectId,
            policy: {
                mode: "conservative",
                minConfidence,
                maxGapSec,
                requireSameSource: true,
                requireSameSpeakerWhenAvailable: true,
            },
            candidates: payload,
        });
        llmResponseReceived = aiRaw != null;
        aiDecisions = parseAiDecisions(aiRaw, candidates);
        usedLlm = llmResponseReceived;
    }

    const candidateById = new Map(candidates.map((c) => [c.id, c]));
    const aiByCandidateId = new Map(aiDecisions.map((d) => [d.candidateId, d]));
    const enforceAiOnly = llmResponseReceived;
    const accepted: RetakeDetection[] = [];

    for (const candidate of candidates) {
        const ai = aiByCandidateId.get(candidate.id);
        if (enforceAiOnly && !ai) continue;
        const fallbackConfidence = clamp(candidate.heuristicScore * 0.92, 0, 0.99);

        const decision: "retake" | "not_retake" = ai
            ? ai.decision
            : candidate.heuristicScore >= 0.93
              ? "retake"
              : "not_retake";
        const remove: "first" | "second" = ai?.remove ?? candidate.suggestedRemove;
        const confidence =
            ai && Number.isFinite(ai.confidence) && Number(ai.confidence) > 0
                ? clamp(Number(ai.confidence), 0, 1)
                : fallbackConfidence;
        if (decision !== "retake" || confidence < minConfidence) continue;

        const removed = remove === "first" ? candidate.firstSegment : candidate.secondSegment;
        const kept = remove === "first" ? candidate.secondSegment : candidate.firstSegment;
        const reason =
            ai?.reason ||
            (candidate.restartCueNearby
                ? "Immediate restart cue plus repeated intent."
                : "Immediate repeated phrasing with same speaker/source.");

        accepted.push({
            id: `retake_${candidate.id}`,
            candidateId: candidate.id,
            startSec: removed.startSec,
            endSec: removed.endSec,
            sourceIndex: removed.sourceIndex,
            speakerKey: removed.speakerKey,
            confidence,
            reason,
            remove,
            removedText: removed.text,
            keptText: kept.text,
            lexicalSimilarity: candidate.lexicalSimilarity,
            restartCueNearby: candidate.restartCueNearby,
        });
    }

    if (useAudioVeto && scriptClip && accepted.length > 0) {
        const filtered: RetakeDetection[] = [];
        for (const d of accepted) {
            if (d.confidence >= AUDIO_VETO_BORDERLINE_CONF) {
                filtered.push(d);
                continue;
            }
            const candidate = candidateById.get(d.candidateId);
            if (!candidate) {
                filtered.push(d);
                continue;
            }
            const envSim = await computeEnvelopeSimilarity(scriptClip, candidate);
            const shouldVeto =
                envSim != null &&
                envSim < AUDIO_VETO_LOW_ENVELOPE_SIM &&
                candidate.lexicalSimilarity < 0.88 &&
                !candidate.restartCueNearby;
            if (!shouldVeto) filtered.push(d);
        }
        return {
            detections: dedupeDetections(filtered),
            stats: {
                totalWords: words.length,
                totalSegments: segments.length,
                totalCandidates: candidates.length,
                usedLlm,
            },
        };
    }

    return {
        detections: dedupeDetections(accepted),
        stats: {
            totalWords: words.length,
            totalSegments: segments.length,
            totalCandidates: candidates.length,
            usedLlm,
        },
    };
}

export async function applyRetakeDetections(args: ApplyRetakesArgs): Promise<{ appliedCount: number }> {
    const detections = Array.isArray(args.detections) ? [...args.detections] : [];
    if (!detections.length) return { appliedCount: 0 };

    const mode: CutTranscriptMode = args.transcriptMode ?? "retake-strikethrough";
    const ordered = dedupeDetections(detections).sort((a, b) => b.startSec - a.startSec);
    let appliedCount = 0;

    CompositionService.beginScriptLayerBatch();
    try {
        for (const d of ordered) {
            const s = Number(d.startSec ?? NaN);
            const e = Number(d.endSec ?? NaN);
            if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s + EPS) continue;
            try {
                await CompositionService.cutScriptLayerRangeWithAnalysis(s, e, d.sourceIndex, {
                    transcriptMode: mode,
                });
            } catch {
                CompositionService.cutScriptLayerRange(s, e, { transcriptMode: mode });
            }
            appliedCount += 1;
        }
    } finally {
        await CompositionService.endScriptLayerBatch();
    }

    return { appliedCount };
}
